/**
 * PWA por clube — cada clube do plano Clube Pro pode ser instalado como app
 * própria, com o nome e o logótipo dele, a abrir direto na sua página.
 *
 * Funciona porque o que define uma app instalável é o manifest: com `id`,
 * `start_url` e `scope` diferentes, o telemóvel trata-a como app separada da
 * PadelMaster.club, e as duas convivem no ecrã inicial.
 *
 * Já não há lista de clubes em lado nenhum: o manifest é gerado a partir dos
 * dados do clube (netlify/functions/manifest.mts) e é ele que decide, pelo
 * plano do dono, se a app é do clube ou a genérica.
 */

import { API_BASE_URL as BACKEND } from '@/config/api';
import { lerAccessToken } from '@/security/tokens';
import { jaInstalada } from './installPrompt';
import { iconeDoClube, versaoDoClube } from './iconeClube';

export interface ClubeParaPwa {
  slug?: string;
  name: string;
  logo?: string | null;
  /** Vem do backend: só o plano Clube Pro dá app própria. */
  app_propria?: boolean;
}

export const clubeTemPwa = (clube?: ClubeParaPwa | null) => !!clube?.app_propria;

/** URL do manifest deste clube, com versão para não vir do cache um antigo. */
export const manifestDoClube = (clube: ClubeParaPwa) =>
  `/manifest/${clube.slug}.webmanifest?v=${versaoDoClube(clube.name, clube.logo)}`;

/**
 * Troca o manifest da página enquanto se está no clube e repõe o original à
 * saída — senão a app principal ficava a apontar para o manifest do clube.
 * Devolve a função de limpeza (para usar direto num useEffect).
 */
export function aplicarManifestDoClube(clube?: ClubeParaPwa | null): () => void {
  if (!clube?.slug || !clube.app_propria) return () => {};

  const linkManifest = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  const linkIcone = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
  // O iPhone tira o nome do atalho daqui (e do <title>), nunca do manifest —
  // sem isto o ícone do clube ficava chamado "Padel".
  const metaNome = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');

  // Se a página já abriu num clube, o href atual JÁ é o do clube (index.html
  // trocou-o cedo) — o original guardado lá é que serve para repor.
  const manifestOriginal =
    (window as unknown as { __pmManifestBase?: string }).__pmManifestBase ??
    linkManifest?.getAttribute('href') ??
    null;
  const iconeOriginal = linkIcone?.getAttribute('href') ?? null;
  const nomeOriginal = metaNome?.getAttribute('content') ?? null;
  const tituloOriginal = document.title;

  linkManifest?.setAttribute('href', manifestDoClube(clube));
  linkIcone?.setAttribute('href', iconeDoClube(clube.logo, 180));
  metaNome?.setAttribute('content', clube.name);
  document.title = clube.name;

  return () => {
    if (manifestOriginal) linkManifest?.setAttribute('href', manifestOriginal);
    if (iconeOriginal) linkIcone?.setAttribute('href', iconeOriginal);
    if (nomeOriginal) metaNome?.setAttribute('content', nomeOriginal);
    document.title = tituloOriginal;
  };
}

/**
 * Em que app do clube é que estamos, se é que estamos em alguma.
 *
 * O `display-mode: standalone` só diz "sou uma app instalada" — e isso é
 * verdade para QUALQUER app deste domínio. Quem tenha a PadelMaster instalada
 * e navegue lá dentro até à página de um clube passava por dono da app desse
 * clube, sem nunca a ter instalado.
 *
 * O que distingue é o `start_url` do manifest, que leva `?app=<slug>`: esse
 * parâmetro só aparece quando o arranque veio do ícone daquele clube. Guarda-se
 * na sessão porque ao primeiro salto de rota o parâmetro desaparece do URL.
 */
const CHAVE_APP = 'pm_app_ativa';

/** Corre uma vez no arranque, antes de qualquer navegação. */
export function registarAppDeArranque(): void {
  try {
    const app = new URLSearchParams(window.location.search).get('app');
    if (app) sessionStorage.setItem(CHAVE_APP, app);
  } catch {
    /* sem sessionStorage: fica-se sem saber, e assume-se que não */
  }
}

/** Slug do clube cuja app está aberta, ou null se estamos na PadelMaster. */
export function slugDaAppAtiva(): string | null {
  if (!jaInstalada()) return null;
  try {
    return sessionStorage.getItem(CHAVE_APP);
  } catch {
    return null;
  }
}

export function estaNaAppDoClube(slug?: string): boolean {
  if (!slug || !jaInstalada()) return false;
  try {
    return sessionStorage.getItem(CHAVE_APP) === slug;
  } catch {
    return false;
  }
}

/**
 * Diz ao backend que esta app abriu: qual é, de que clube e em que versão.
 *
 * Serve para saber quantos sócios de um clube têm mesmo a app dele no ecrã
 * inicial — que é o argumento de venda do plano Clube Pro — e com que versão
 * andam. Uma vez por sessão chega: o backend guarda uma linha por dispositivo
 * e por app, e só lhe mexe na data.
 *
 * Falha em silêncio de propósito: isto é registo, não é funcionalidade, e não
 * pode ser motivo para a app do clube deixar de abrir.
 */
const CHAVE_DISPOSITIVO = 'pm_dispositivo';
const CHAVE_JA_ENVIADO = 'pm_app_registada';

const idDoDispositivo = (): string => {
  try {
    let id = localStorage.getItem(CHAVE_DISPOSITIVO);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(CHAVE_DISPOSITIVO, id);
    }
    return id;
  } catch {
    return '';
  }
};

export async function registarAberturaNoBackend(versao: string, clubSlug?: string): Promise<void> {
  if (!jaInstalada()) return; // no browser não há app instalada para registar

  // Só a app do clube conta como app do clube: estar dentro da PadelMaster e
  // navegar até um clube não é ter a app dele.
  const tipo = clubSlug && estaNaAppDoClube(clubSlug) ? 'clube' : 'padelmaster';
  const dispositivo = idDoDispositivo();
  if (!dispositivo) return;

  const marca = `${CHAVE_JA_ENVIADO}_${tipo}_${clubSlug || ''}`;
  try {
    if (sessionStorage.getItem(marca)) return;
    sessionStorage.setItem(marca, '1');
  } catch {
    /* sem sessionStorage manda-se na mesma; o backend faz update, não duplica */
  }

  try {
    // Com sessão iniciada, manda-se o token: sem ele o backend vê um anónimo e
    // o registo fica sem saber de quem é o telemóvel. O endpoint aceita os dois
    // casos de propósito — a app do clube abre-se sem conta.
    const token = lerAccessToken();
    await fetch(`${BACKEND}/padel/app-aberta/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        dispositivo,
        tipo,
        versao,
        club_slug: tipo === 'clube' ? clubSlug : undefined,
      }),
    });
  } catch {
    /* offline ou backend em baixo: não interessa nada para o utilizador */
  }
}
